import React, { useState } from "react";
import { navigate } from "@reach/router";
import { AuthConsumer } from "../helpers/AuthContext";

const STEPS = [
  { id: 1, name: "Welcome" },
  { id: 2, name: "How it works" },
  { id: 3, name: "Get started" },
];

const Stepper = ({ current }) => (
  <div className="onb-stepper">
    {STEPS.map((s, i) => {
      const state =
        s.id === current ? "active" : s.id < current ? "done" : "pending";
      return (
        <React.Fragment key={s.id}>
          <div
            className={`onb-stepper__item ${
              state === "active"
                ? "onb-stepper__item--active"
                : state === "done"
                ? "onb-stepper__item--done"
                : ""
            }`}
          >
            <span className="onb-stepper__num">{s.id}</span>
            <span>{s.name}</span>
          </div>
          {i < STEPS.length - 1 && <div className="onb-stepper__bar" />}
        </React.Fragment>
      );
    })}
  </div>
);

const Onboarding = () => {
  const [step, setStep] = useState(1);

  const next = () => setStep((s) => Math.min(3, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  return (
    <AuthConsumer>
      {({ Name }) => {
        const displayName = Name && Name !== "Guest" ? Name : "labeler";

        return (
          <div className="theme-scientific onboarding-page">
            <div className="onboarding-shell">
              <Stepper current={step} />

              {/* ================= STEP 1 ================= */}
              {step === 1 && (
                <div className="onb-card" key="step-1">
                  <div className="onb-card__eyebrow">Step 1 of 3</div>
                  <h1 className="onb-card__title">
                    Welcome, <em>{displayName}</em>.
                  </h1>
                  <p className="onb-card__body">
                    You've joined an open project for labeling EEG components.
                    Your labels help train automated classifiers for brain
                    research.
                  </p>

                  <div className="onb-actions">
                    <span />
                    <div className="onb-actions__right">
                      <button
                        className="sci-btn sci-btn--primary"
                        onClick={next}
                      >
                        Continue →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ================= STEP 2 ================= */}
              {step === 2 && (
                <div className="onb-card" key="step-2">
                  <div className="onb-card__eyebrow">Step 2 of 3</div>
                  <h1 className="onb-card__title">How labeling works</h1>
                  <p className="onb-card__body">
                    Each task shows one independent component from a real EEG
                    recording. You decide what it is.
                  </p>

                  <ol className="onb-list">
                    <li>Inspect the topography, time series, and power spectrum.</li>
                    <li>Select <strong>every</strong> category that applies: brain, muscle, eye, heart, line noise, channel noise, or other.</li>
                    <li>Submit. Your label joins the community consensus.</li>
                  </ol>

                  <p className="onb-card__body" style={{ marginTop: 12, fontWeight: 600 }}>
                    A component is not limited to one label — when it shows
                    characteristics of more than one class (e.g. brain + eye),
                    you can and should select all of them.
                  </p>

                  <div className="onb-actions">
                    <button className="onb-actions__skip" onClick={back}>
                      ← Back
                    </button>
                    <div className="onb-actions__right">
                      <button
                        className="sci-btn sci-btn--primary"
                        onClick={next}
                      >
                        Continue →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ================= STEP 3 ================= */}
              {step === 3 && (
                <div className="onb-card" key="step-3">
                  <div className="onb-card__eyebrow">Step 3 of 3</div>
                  <h1 className="onb-card__title">Before you can label</h1>
                  <p className="onb-card__body">
                    You need to do two things first.
                  </p>

                  <ol className="onb-list">
                    <li>
                      <strong>Read the tutorial.</strong> A walk-through of
                      each component class with examples. About 10 minutes.
                    </li>
                    <li>
                      <strong>Complete the practice round.</strong> Label 10
                      ICs to unlock live labeling — your accuracy is shown
                      after you finish, but there's no minimum score.
                    </li>
                  </ol>

                  <p className="onb-card__body" style={{ marginTop: 16 }}>
                    Once both are done, the live labeling queue unlocks.
                  </p>

                  <div className="onb-actions">
                    <button className="onb-actions__skip" onClick={back}>
                      ← Back
                    </button>
                    <div className="onb-actions__right">
                      <button
                        className="sci-btn sci-btn--primary"
                        onClick={() => navigate("/tutorial")}
                      >
                        Start tutorial →
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }}
    </AuthConsumer>
  );
};

export default Onboarding;
